import React, { useState } from 'react';
import { mfaVerify, mfaRecoveryVerify } from '../services/api';
import { Shield, AlertCircle, X } from 'lucide-react';
import './MFAVerificationModal.css';

/**
 * MFA Verification Modal - Shown during login when MFA verification is required.
 */
export const MFAVerificationModal = ({ isOpen, onClose, onSuccess, userEmail }) => {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Verification code is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (useRecovery) {
        response = await mfaRecoveryVerify(code.trim());
      } else {
        response = await mfaVerify(code.trim(), false);
      }

      // On success, call the onSuccess callback with tokens
      if (response.data.access_token) {
        onSuccess(response.data);
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Invalid verification code'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    if (useRecovery) {
      // Recovery codes can be longer, allow alphanumeric
      setCode(value);
    } else {
      // TOTP codes are 6 digits
      setCode(value.replace(/\D/g, '').slice(0, 6));
    }
  };

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setCode('');
      setUseRecovery(false);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="mfa-verification-modal-overlay" onClick={onClose}>
      <div className="mfa-verification-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mfa-verification-header">
          <Shield size={24} />
          <div>
            <h2>Multi-Factor Authentication</h2>
            {userEmail && <p className="mfa-verification-subtitle">Signing in as {userEmail}</p>}
          </div>
          {onClose && (
            <button className="mfa-verification-close" onClick={onClose}>
              <X size={20} />
            </button>
          )}
        </div>

        <p className="mfa-verification-description">
          {useRecovery
            ? 'Enter one of your recovery codes to complete sign-in.'
            : 'Enter the 6-digit code from your authenticator app to complete sign-in.'}
        </p>

        {error && (
          <div className="mfa-verification-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              {useRecovery ? 'Recovery Code' : 'Verification Code'} *
            </label>
            <input
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder={useRecovery ? 'Enter recovery code' : '000000'}
              required
              autoFocus
              disabled={loading}
              className={useRecovery ? '' : 'mfa-code-input'}
              maxLength={useRecovery ? undefined : 6}
            />
            {!useRecovery && (
              <small>Enter the 6-digit code from your authenticator app</small>
            )}
          </div>

          <div className="mfa-verification-toggle">
            <label className="mfa-toggle-label">
              <input
                type="checkbox"
                checked={useRecovery}
                onChange={(e) => {
                  setUseRecovery(e.target.checked);
                  setCode('');
                  setError(null);
                }}
              />
              <span>Use recovery code instead</span>
            </label>
          </div>

          <div className="mfa-verification-actions">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !code.trim() || (!useRecovery && code.length !== 6)}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
